import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PrinterIcon,
  ReceiptTaxIcon,
  UserIcon,
  CalendarIcon,
} from "@heroicons/react/outline";

import { useEffect, useState, useRef } from "react";
import swal from "sweetalert";
import { selectCurrentUser } from "../../Features/auth/authSlice";
import { useDispatch, useSelector } from "react-redux";
import LoadingBar from "react-top-loading-bar";
import { useIsOnline } from "react-use-is-online";
import ReactPaginate from "react-paginate";

import "../../Pagination.css";
import "./ManageReceiptsView.css";

import { motion, AnimatePresence } from "framer-motion";

import "@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css";
import "react-calendar/dist/Calendar.css";

import { format } from "date-fns";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import { DateRangePicker } from "react-date-range";

import { addDays, subDays } from "date-fns";

import { useGetUserEmployeesQuery } from "../../Features/userEmployeesSlice";

import {
  useGetSalesByPaymentTypeByDateRangeMutation,
  useGetSalesByPaymentTypeQuery,
} from "../../Features/userReportsSlice";

import {
  selectDateRange,
  setDateRange,
} from "../../Features/date/dateSlice";

import { useTheme } from "../../ThemeContext";

function ManageSalesByPaymentTypeView({}) {
  const { darkMode } = useTheme();

  const user = useSelector(selectCurrentUser);
  const date_range = useSelector(selectDateRange);
  const dispatch = useDispatch();

  const [progress, setProgress] = useState(0);
  const { isOnline } = useIsOnline();

  const [numRowsPerPage, setNumRowsPerPage] = useState(10);
  const [currentItems, setCurrentItems] = useState([]);

  const [open, setOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(0);

  const pickerRef = useRef(null);

  const [isExporting, setIsExporting] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);

  const [selectedStartDate, setSelectedStartDate] = useState(false);
  const [selectedEndDate, setSelectedEndDate] = useState(false);

  const [state, setState] = useState([
    {
      startDate: subDays(new Date(), 7),
      endDate: addDays(new Date(), 1),
      key: "selection",
    },
  ]);

  /*
  |--------------------------------------------------------------------------
  | Initial Dates
  |--------------------------------------------------------------------------
  */

  const initialStartDate = date_range
    ? format(date_range.startDate, "yyyy-MM-dd")
    : format(
        subDays(new Date(), 7),
        "yyyy-MM-dd"
      );

  const initialEndDate = date_range
    ? format(date_range.endDate, "yyyy-MM-dd")
    : format(
        subDays(new Date(), 0),
        "yyyy-MM-dd"
      );

  /*
  |--------------------------------------------------------------------------
  | Initial Sales By Payment Type
  |--------------------------------------------------------------------------
  */

  const {
    data: data,
    isLoading,
    isSuccess,
  } = useGetSalesByPaymentTypeQuery({
    user_id: user.id,
    start_date: initialStartDate,
    end_date: initialEndDate,
    employee_id: "0",
  });

  /*
  |--------------------------------------------------------------------------
  | Employees
  |--------------------------------------------------------------------------
  */

  const { data: employees } =
    useGetUserEmployeesQuery({
      account_id: user.id,
      paginate: 10,
    });

  /*
  |--------------------------------------------------------------------------
  | Initial Data Effect
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    setProgress(40);

    if (isLoading) {
      setProgress(80);
    }

    if (isSuccess) {
      setCurrentItems(data.data);
    }

    setProgress(100);

    const handleClickOutside = (event) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, [data]);

  /*
  |--------------------------------------------------------------------------
  | Employee Search
  |--------------------------------------------------------------------------
  */

  const handleEmployeeChange = async (
    e,
    employeeId
  ) => {
    const empId =
      employeeId ?? e.target.value;

    submitSearch(empId);
  };

  /*
  |--------------------------------------------------------------------------
  | Date Range Search
  |--------------------------------------------------------------------------
  */

  const [fetchCustomData] =
    useGetSalesByPaymentTypeByDateRangeMutation();

  const handleSelect = async (ranges) => {
    const { selection } = ranges;

    setState([selection]);

    // First click
    if (!isSelecting) {
      setIsSelecting(true);
      return;
    }

    // Second click
    setIsSelecting(false);
    setOpen(false);

    dispatch(
      setDateRange({
        date_range: selection,
      })
    );

    const startDate = format(
      selection.startDate,
      "yyyy-MM-dd"
    );

    const endDate = format(
      selection.endDate,
      "yyyy-MM-dd"
    );

    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);

    try {
      setProgress(80);

      const resp = await fetchCustomData({
        user_id: user.id,
        start_date: startDate,
        end_date: endDate,
        employee_id: selectedEmployee
          ? selectedEmployee
          : "0",
      }).unwrap();

      if (resp.success) {
        setCurrentItems(resp.data);
      } else {
        swal("No results found.");
      }
    } catch (err) {
      swal("Error", "Data not found");
    } finally {
      setProgress(100);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | CSV Export
  |--------------------------------------------------------------------------
  */

  const handleExportCSV = async () => {
    const startDate =
      selectedStartDate || initialStartDate;

    const endDate =
      selectedEndDate || initialEndDate;

    try {
      setIsExporting(true);

      const response = await fetch(
        `http://127.0.0.1:8000/user/sales-by-payment-type/${user.id}/${startDate}/${endDate}/${selectedEmployee ? selectedEmployee : "0"}`
      );

      const result = await response.json();

      if (
        !result.success ||
        !result.data?.length
      ) {
        swal(
          "No data",
          "No records found to export",
          "info"
        );

        setIsExporting(false);

        return;
      }

      const report = result.data;

      const headers = [
        "Payment Type",
        "Active Tickets Count",
        "Active Tickets Amount",
        "Cancelled Tickets Count",
        "Cancelled Tickets Amount",
        "Total Discounts",
        "Net Amount",
      ];

      const rows = report.map((item) => [
        item.is_total
          ? `**${item.payment_type_name.toUpperCase()}**`
          : item.payment_type_name,
        item.active_tickets_count,
        item.active_tickets_amount,
        item.cancelled_tickets_count,
        item.cancelled_tickets_amount,
        item.discounts,
        item.net_amount,
      ]);

      const csvContent = [headers, ...rows]
        .map((row) =>
          row
            .map(
              (value) =>
                `"${String(value ?? "").replace(
                  /"/g,
                  '""'
                )}"`
            )
            .join(",")
        )
        .join("\n");

      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      const url =
        URL.createObjectURL(blob);

      const link =
        document.createElement("a");

      link.href = url;

      link.download = `sales_by_payment_type_${
        new Date().toISOString().split("T")[0]
      }.csv`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      setIsExporting(false);

      swal(
        "Success",
        "Payment type sales report exported successfully.",
        "success"
      );
    } catch (error) {
      console.error(
        "Payment Type CSV Export Error:",
        error
      );

      swal(
        "Error",
        "Something went wrong while exporting data",
        "error"
      );

      setIsExporting(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Employee Filter
  |--------------------------------------------------------------------------
  */

  const submitSearch = async (
    employeeId = 0
  ) => {
    try {
      setProgress(80);

      const resp =
        await fetchCustomData({
          user_id: user.id,
          start_date: selectedStartDate
            ? selectedStartDate
            : initialStartDate,
          end_date: selectedEndDate
            ? selectedEndDate
            : initialEndDate,
          employee_id: employeeId
            ? employeeId
            : "0",
        }).unwrap();

      if (resp.success) {
        setCurrentItems(resp.data);
      } else {
        swal("No results found.");
      }

      setProgress(100);
    } catch (err) {
      setProgress(100);

      swal(
        "No results found..."
      );
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Formatting
  |--------------------------------------------------------------------------
  */

  const formatMoney = (value) => {
    return Number(
      value ?? 0
    ).toLocaleString("en-ZM", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (
    <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">

      <LoadingBar
        color="#80b643"
        height="4px"
        progress={progress}
        onLoaderFinished={() =>
          setProgress(0)
        }
      />

      <div className="w-full px-3 sm:px-4 lg:px-6 pt-4">

        {/* ============================================================
            HEADER
        ============================================================ */}

        <div className="mb-5">

          <div className="flex items-center gap-3">

            <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20">

              <ReceiptTaxIcon className="w-6 h-6 text-[#80b643]" />

            </div>

            <div>

              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                Sales by Payment Type
              </h1>

              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                View sales performance by payment method
              </p>

            </div>

          </div>

        </div>

        {/* ============================================================
            FILTER CARD
        ============================================================ */}

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm mb-5">

          <div className="p-4">

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">

              {/* Report Description */}

              <div>

                <p className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  Report period
                </p>

                <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mt-1">
                  Select a date range and employee
                </p>

              </div>

              {/* Filters */}

              <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">

                {/* Date Picker */}

                <div
                  ref={pickerRef}
                  className="relative w-full sm:w-auto"
                >

                  <button
                    type="button"
                    onClick={() =>
                      setOpen(!open)
                    }
                    className="
                      w-full sm:w-auto
                      flex items-center justify-between sm:justify-start
                      gap-3
                      bg-gray-50 dark:bg-gray-800
                      border border-gray-200 dark:border-gray-700
                      hover:border-[#80b643]
                      text-gray-700 dark:text-gray-200
                      px-4 py-2.5
                      rounded-lg
                      text-sm
                      transition
                    "
                  >

                    <div className="flex items-center gap-2">

                      <CalendarIcon className="w-4 h-4 text-[#80b643]" />

                      <span>

                        {format(
                          date_range
                            ? date_range.startDate
                            : state[0].startDate,
                          "MMM dd, yyyy"
                        )}

                        <span className="mx-2 text-gray-400">
                          →
                        </span>

                        {format(
                          date_range
                            ? date_range.endDate
                            : state[0].endDate,
                          "MMM dd, yyyy"
                        )}

                      </span>

                    </div>

                    <ChevronDownIcon
                      className={`w-4 h-4 transition-transform ${
                        open
                          ? "rotate-180"
                          : ""
                      }`}
                    />

                  </button>

                  <AnimatePresence>

                    {open && (
                      <motion.div
                        initial={{
                          opacity: 0,
                          y: -8,
                          scale: 0.98,
                        }}
                        animate={{
                          opacity: 1,
                          y: 0,
                          scale: 1,
                        }}
                        exit={{
                          opacity: 0,
                          y: -8,
                          scale: 0.98,
                        }}
                        transition={{
                          duration: 0.2,
                        }}
                        className="
                          absolute
                          z-50
                          right-0
                          mt-2
                          bg-white
                          dark:bg-gray-900
                          border
                          border-gray-200
                          dark:border-gray-700
                          rounded-xl
                          shadow-2xl
                          overflow-hidden
                        "
                      >

                        <DateRangePicker
                          onChange={
                            handleSelect
                          }
                          showSelectionPreview={
                            true
                          }
                          moveRangeOnFirstSelection={
                            false
                          }
                          months={1}
                          ranges={state}
                          direction="horizontal"
                        />

                      </motion.div>
                    )}

                  </AnimatePresence>

                </div>

                {/* Employee Filter */}

                <div className="relative w-full sm:w-auto">

                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#80b643] pointer-events-none z-10" />

                  <select
                    value={
                      selectedEmployee
                    }
                    onChange={(e) => {
                      setSelectedEmployee(
                        e.target.value
                      );

                      handleEmployeeChange(
                        e,
                        e.target.value
                      );
                    }}
                    className="
                      appearance-none
                      w-full sm:w-[220px]
                      bg-gray-50
                      dark:bg-gray-800
                      text-gray-700
                      dark:text-gray-200
                      border
                      border-gray-200
                      dark:border-gray-700
                      pl-9
                      pr-9
                      py-2.5
                      rounded-lg
                      text-sm
                      hover:border-[#80b643]
                      cursor-pointer
                      focus:outline-none
                      focus:border-[#80b643]
                      transition
                    "
                  >

                    <option value="">
                      All Employees
                    </option>

                    {employees?.data?.data?.map(
                      (item) => (
                        <option
                          key={item.id}
                          value={item.id}
                        >
                          {item.fname}{" "}
                          {item.lname}
                        </option>
                      )
                    )}

                  </select>

                  <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />

                </div>

              </div>

            </div>

          </div>

        </div>

        {/* ============================================================
            PAYMENT TYPE TABLE
        ============================================================ */}

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

          {currentItems &&
          currentItems.length > 0 ? (

            <>

              {/* Table Header */}

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-5 py-4 border-b border-gray-100 dark:border-gray-800">

                <div>

                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Payment Type Performance
                  </h2>

                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Breakdown of transactions by payment method
                  </p>

                </div>

                {/* Export */}

                <button
                  type="button"
                  onClick={
                    handleExportCSV
                  }
                  disabled={isExporting}
                  className={`
                    inline-flex
                    items-center
                    justify-center
                    gap-2
                    px-4
                    py-2
                    rounded-lg
                    text-sm
                    font-medium
                    border
                    transition
                    ${
                      isExporting
                        ? `
                          bg-gray-200
                          dark:bg-gray-800
                          text-gray-400
                          dark:text-gray-500
                          border-gray-200
                          dark:border-gray-700
                          cursor-not-allowed
                        `
                        : `
                          bg-[#80b643]
                          hover:bg-[#719f3a]
                          text-white
                          border-[#80b643]
                          shadow-sm
                        `
                    }
                  `}
                >

                  <PrinterIcon className="w-4 h-4" />

                  {isExporting
                    ? "Exporting..."
                    : "Export CSV"}

                </button>

              </div>

              {/* Responsive Table */}

              <div className="overflow-x-auto">

                <table className="min-w-[1050px] w-full">

                  <thead>

                    <tr className="bg-gray-50 dark:bg-gray-800/70 border-b border-gray-100 dark:border-gray-800">

                      <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Payment Type
                      </th>

                      <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Payment Transactions
                      </th>

                      <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Payment Amount
                      </th>

                      <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Refund Transactions
                      </th>

                      <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Total Refunds
                      </th>

                      <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Total Discounts
                      </th>

                      <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Net Amount
                      </th>

                    </tr>

                  </thead>

                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">

                    {currentItems.map(
                      (data, index) => {

                        const isTotal =
                          data.is_total;

                        return (
                          <tr
                            key={
                              data.payment_type_name ||
                              index
                            }
                            className={`
                              transition
                              ${
                                isTotal
                                  ? `
                                    bg-gray-50
                                    dark:bg-gray-800/60
                                    border-t-2
                                    border-[#80b643]
                                  `
                                  : `
                                    hover:bg-gray-50
                                    dark:hover:bg-gray-800/60
                                  `
                              }
                            `}
                          >

                            {/* Payment Type */}

                            <td className="px-5 py-4">

                              <div className="flex items-center gap-3">

                                <div
                                  className={`
                                    w-8
                                    h-8
                                    rounded-lg
                                    flex
                                    items-center
                                    justify-center
                                    flex-shrink-0
                                    ${
                                      isTotal
                                        ? "bg-[#80b643]/15 dark:bg-[#80b643]/20"
                                        : "bg-gray-100 dark:bg-gray-800"
                                    }
                                  `}
                                >

                                  <ReceiptTaxIcon
                                    className={`
                                      w-4
                                      h-4
                                      ${
                                        isTotal
                                          ? "text-[#80b643]"
                                          : "text-gray-400 dark:text-gray-500"
                                      }
                                    `}
                                  />

                                </div>

                                <div>

                                  <p
                                    className={`
                                      text-sm
                                      ${
                                        isTotal
                                          ? "font-semibold text-gray-900 dark:text-white"
                                          : "font-medium text-gray-800 dark:text-gray-100"
                                      }
                                    `}
                                  >
                                    {isTotal
                                      ? data.payment_type_name.toUpperCase()
                                      : data.payment_type_name}
                                  </p>

                                  {isTotal && (
                                    <p className="text-[11px] mt-0.5 font-medium">
                                      
                                    </p>
                                  )}

                                </div>

                              </div>

                            </td>

                            {/* Payment Transactions */}

                            <td className="px-5 py-4 text-right">

                              <span
                                className={`
                                  text-sm
                                  ${
                                    isTotal
                                      ? "font-semibold text-gray-900 dark:text-white"
                                      : "text-gray-600 dark:text-gray-300"
                                  }
                                `}
                              >
                                {Number(
                                  data.active_tickets_count ??
                                    0
                                ).toLocaleString(
                                  "en-US"
                                )}
                              </span>

                            </td>

                            {/* Payment Amount */}

                            <td className="px-5 py-4 text-right">

                              <span
                                className={`
                                  text-sm
                                  whitespace-nowrap
                                  ${
                                    isTotal
                                      ? "font-semibold"
                                      : "text-gray-700 dark:text-gray-200"
                                  }
                                `}
                              >

                                K{" "}
                                {formatMoney(
                                  data.active_tickets_amount
                                )}

                              </span>

                            </td>

                            {/* Refund Transactions */}

                            <td className="px-5 py-4 text-right">

                              <span
                                className={`
                                  text-sm
                                  ${
                                    isTotal
                                      ? "font-semibold text-gray-900 dark:text-white"
                                      : "text-gray-600 dark:text-gray-300"
                                  }
                                `}
                              >
                                {Number(
                                  data.cancelled_tickets_count ??
                                    0
                                ).toLocaleString(
                                  "en-US"
                                )}
                              </span>

                            </td>

                            {/* Total Refunds */}

                            <td className="px-5 py-4 text-right">

                              <span
                                className={`
                                  text-sm
                                  whitespace-nowrap
                                  ${
                                    isTotal
                                      ? "font-semibold text-gray-900 dark:text-white"
                                      : "text-gray-700 dark:text-gray-200"
                                  }
                                `}
                              >

                                K{" "}
                                {formatMoney(
                                  data.cancelled_tickets_amount
                                )}

                              </span>

                            </td>

                            {/* Discounts */}

                            <td className="px-5 py-4 text-right">

                              <span
                                className={`
                                  text-sm
                                  whitespace-nowrap
                                  ${
                                    isTotal
                                      ? "font-semibold text-gray-900 dark:text-white"
                                      : "text-gray-700 dark:text-gray-200"
                                  }
                                `}
                              >

                                K{" "}
                                {formatMoney(
                                  data.discounts
                                )}

                              </span>

                            </td>

                            {/* Net Amount */}

                            <td className="px-5 py-4 text-right">

                              <span
                                className={`
                                  text-sm
                                  whitespace-nowrap
                                  ${
                                    isTotal
                                      ? "font-semibold text-gray-900 dark:text-white"
                                      : "text-gray-700 dark:text-gray-200"
                                  }
                                `}
                              >

                                K{" "}
                                {formatMoney(
                                  data.net_amount
                                )}

                              </span>

                            </td>

                          </tr>
                        );
                      }
                    )}

                  </tbody>

                </table>

              </div>

            </>

          ) : (

            /* ==========================================================
               EMPTY STATE
            ========================================================== */

            <div className="flex flex-col items-center justify-center py-16 px-5">

              <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">

                <ReceiptTaxIcon className="w-8 h-8 text-gray-400 dark:text-gray-500" />

              </div>

              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                No payment records found
              </h3>

              <p className="text-xs text-gray-500 dark:text-gray-400 text-center max-w-sm mt-1">
                There are no payment type sales records available
                for the selected date range.
              </p>

            </div>

          )}

        </div>

      </div>

    </div>
  );
}

export default ManageSalesByPaymentTypeView;